"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  Checkbox,
  CopyButton,
  DataTable,
  Notice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SMALL,
  CARD,
  HINT,
  INPUT,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  PaperControls,
  PRINTABLES_COMMON_STRINGS,
  PrintablePages,
  PrintBar,
  useTextMeasure,
} from "@/components/tools/printables/page-kit";
import { HabitSheetArt } from "@/components/tools/study-more/habit-sheet-art";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  daysInMonth,
  monthName,
  weekday,
  weekdayNames,
} from "@/lib/tool-kit/printables/calendar";
import { type PaperSize, pageGeometry } from "@/lib/tool-kit/printables/page";
import { dayParts, localToday, randomToken } from "@/lib/tool-kit/study/dates";
import { layoutHabitSheet } from "@/lib/tool-kit/study-more/habit-sheet";
import {
  currentStreak,
  type HabitState,
  habitsText,
  isoOfMonthDay,
  longestStreak,
  MAX_HABITS,
  MAX_NAME_LENGTH,
  monthSummary,
  parseSavedHabits,
  tickedDays,
  toggleIso,
} from "@/lib/tool-kit/study-more/habits";

const STORAGE_KEY = "citationeasy-habit-tracker-v1";
const BLANK_ROW_CHOICES = ["0", "3", "5", "8", "10"] as const;
const ACCENT = "#1f4e79";
/** Shown until the browser reports today's date, so server and client markup match. */
const FALLBACK_VIEW = { year: 2026, month: 0 };

/** English strings. A translated content file overrides these through `ui`. */
export const HABIT_TRACKER_STRINGS = {
  ...PRINTABLES_COMMON_STRINGS,
  trackerHeading: "Your habits this month",
  previousMonth: "Previous month",
  nextMonth: "Next month",
  thisMonth: "Go to this month",
  newHabitLabel: "New habit",
  newHabitPlaceholder: "e.g. Read for 20 minutes",
  addHabit: "Add habit",
  limitReached: "You can track up to {max} habits at once.",
  habitColumn: "Habit",
  habitNameLabel: "Habit name, row {number}",
  removeHabit: "Remove {name}",
  confirmRemove: "Click again to remove {name} and its ticks",
  untitledHabit: "Habit {number}",
  cellLabel: "{habit}, {date}",
  tickedState: "done",
  emptyState:
    "Add your first habit above — a habit small enough to do on your worst day works best.",
  statsHeading: "Streaks and totals",
  statsHabit: "Habit",
  statsMonth: "This month",
  statsRate: "Rate",
  statsCurrent: "Current streak",
  statsLongest: "Longest streak",
  monthDone: "{done} of {elapsed} days",
  dayCount: "one:{count} day|other:{count} days",
  rateValue: "{percent}%",
  notStarted: "—",
  streakNote:
    "A streak counts consecutive days ending today. A habit you have not ticked yet today keeps its streak until the day is over; missing a whole day ends it.",
  copyText: "Copy summary",
  copied: "Copied!",
  summaryTitle: "Habit summary — {month}",
  summaryDone: "ticked",
  summaryStreak: "current streak",
  summaryLongest: "longest streak",
  storageNote:
    "Your habits and ticks are saved in this browser's local storage — nothing is uploaded. They stay on this device and browser, and clearing site data erases them.",
  storageUnavailable:
    "This browser is not letting the page save data, so your ticks will be gone when you close the page. Print the sheet or copy the summary first.",
  printHeading: "Print a monthly sheet",
  sheetHint:
    "The sheet shows the month on screen above. Habits you have added are written in; blank rows are for habits you write by hand.",
  sheetTitleLabel: "Sheet title",
  defaultTitle: "Habit Tracker",
  blankRowsLabel: "Blank rows to add",
  includeTicks: "Print my ticks too",
  sheetNameHeading: "Habit",
  sheetTotalHeading: "Total",
  previewLabel: "Habit tracker sheet preview",
  tooManyRows:
    "Rows are squeezed to fit — choose fewer blank rows for roomier cells.",
  fileName: "habit-tracker",
} as const;

interface View {
  year: number;
  month: number;
}

function initials(locale: string): string[] {
  return weekdayNames(locale, 0, "narrow");
}

export default function HabitTrackerWidget() {
  const strings = useToolUi(HABIT_TRACKER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const measure = useTextMeasure();
  const svgRef = useRef<SVGSVGElement>(null);
  const [state, setState] = useState<HabitState>({ habits: [], done: {} });
  const [loaded, setLoaded] = useState(false);
  const [canSave, setCanSave] = useState(true);
  const [today, setToday] = useState<number | null>(null);
  const [view, setView] = useState<View>(FALLBACK_VIEW);
  const [newName, setNewName] = useState("");
  const [armed, setArmed] = useState<string | null>(null);
  const [title, setTitle] = useState<string>(strings.defaultTitle);
  const [blankRows, setBlankRows] = useState<string>("5");
  const [showTicks, setShowTicks] = useState(true);
  const [paper, setPaper] = useState<PaperSize>("letter");

  useEffect(() => {
    let raw: string | null = null;
    try {
      raw = window.localStorage.getItem(STORAGE_KEY);
    } catch {
      setCanSave(false);
    }
    setState(parseSavedHabits(raw));
    const day = localToday();
    const parts = dayParts(day);
    setToday(day);
    setView({ year: parts.year, month: parts.month - 1 });
    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!(loaded && canSave)) {
      return;
    }
    const timer = setTimeout(() => {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
      } catch {
        setCanSave(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [loaded, canSave, state]);

  const length = daysInMonth(view.year, view.month);
  const monthLabel = `${monthName(locale, view.month)} ${view.year}`;
  const firstWeekday = weekday(view.year, view.month, 1);
  const initialNames = useMemo(() => initials(locale), [locale]);
  const dayFormat = useMemo(
    () =>
      new Intl.DateTimeFormat(locale, {
        weekday: "long",
        month: "long",
        day: "numeric",
        timeZone: "UTC",
      }),
    [locale]
  );
  const todayIso = today === null ? null : dayPartsIso(today);

  function dayPartsIso(day: number): string {
    const parts = dayParts(day);
    return isoOfMonthDay(parts.year, parts.month - 1, parts.day) ?? "";
  }

  function habitName(index: number): string {
    const name = state.habits[index]?.name.trim();
    return name || t(strings.untitledHabit, { number: index + 1 });
  }

  function addHabit() {
    const name = newName.trim().slice(0, MAX_NAME_LENGTH);
    if (!name || state.habits.length >= MAX_HABITS) {
      return;
    }
    setState({
      ...state,
      habits: [...state.habits, { id: randomToken(8), name }],
    });
    setNewName("");
  }

  function renameHabit(id: string, name: string) {
    setState({
      ...state,
      habits: state.habits.map((habit) =>
        habit.id === id
          ? { ...habit, name: name.slice(0, MAX_NAME_LENGTH) }
          : habit
      ),
    });
  }

  function removeHabit(id: string) {
    if (armed !== id) {
      setArmed(id);
      return;
    }
    const done = { ...state.done };
    delete done[id];
    setState({ habits: state.habits.filter((habit) => habit.id !== id), done });
    setArmed(null);
  }

  function toggle(id: string, iso: string) {
    setState({
      ...state,
      done: { ...state.done, [id]: toggleIso(state.done[id], iso) },
    });
  }

  function shiftMonth(delta: number) {
    const index = view.year * 12 + view.month + delta;
    setView({ year: Math.floor(index / 12), month: ((index % 12) + 12) % 12 });
  }

  function goToday() {
    if (today !== null) {
      const parts = dayParts(today);
      setView({ year: parts.year, month: parts.month - 1 });
    }
  }

  const rows = state.habits.map((habit) => {
    const days = tickedDays(state.done[habit.id]);
    const summary = monthSummary(days, view.year, view.month, length, today);
    return {
      habit,
      days,
      summary,
      current: today === null ? 0 : currentStreak(days, today),
      longest: longestStreak(days),
    };
  });

  const summaryText = habitsText(
    rows.map((row, index) => ({
      name: habitName(index),
      done: row.summary.done,
      elapsed: row.summary.elapsed,
      current: row.current,
      longest: row.longest,
    })),
    {
      title: t(strings.summaryTitle, { month: monthLabel }),
      done: strings.summaryDone,
      streak: strings.summaryStreak,
      longest: strings.summaryLongest,
    }
  );

  const geometry = pageGeometry(paper, "landscape");
  const blank = Number(blankRows);
  const sheetNames = [
    ...state.habits.map((habit) => habit.name),
    ...Array.from({ length: blank }, () => ""),
  ];
  const sheetTicks = showTicks
    ? rows.map((row) =>
        Array.from({ length }, (_, index) => {
          const iso = isoOfMonthDay(view.year, view.month, index + 1);
          return iso !== null && (state.done[row.habit.id] ?? []).includes(iso);
        })
      )
    : null;
  const layout = layoutHabitSheet(geometry, measure, {
    title: title.trim() || strings.defaultTitle,
    monthLabel,
    dayCount: length,
    firstWeekday,
    weekdayInitials: initialNames,
    habits: sheetNames.length > 0 ? sheetNames : [""],
    ticks: sheetTicks,
    nameHeading: strings.sheetNameHeading,
    totalHeading: strings.sheetTotalHeading,
  });

  return (
    <div className="space-y-8 print:space-y-0">
      <section className="space-y-5 print:hidden">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className={WIDGET_H2}>{strings.trackerHeading}</h2>
          <div className="flex flex-wrap items-center gap-2">
            <button
              aria-label={strings.previousMonth}
              className={BUTTON_SMALL}
              onClick={() => shiftMonth(-1)}
              type="button"
            >
              ‹
            </button>
            <span
              aria-live="polite"
              className="min-w-36 text-center font-medium text-foreground text-sm"
            >
              {monthLabel}
            </span>
            <button
              aria-label={strings.nextMonth}
              className={BUTTON_SMALL}
              onClick={() => shiftMonth(1)}
              type="button"
            >
              ›
            </button>
            <button className={BUTTON_SMALL} onClick={goToday} type="button">
              {strings.thisMonth}
            </button>
          </div>
        </div>

        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(event) => {
            event.preventDefault();
            addHabit();
          }}
        >
          <div className="min-w-56 flex-1">
            <TextField
              id="ht-new"
              label={strings.newHabitLabel}
              onChange={setNewName}
              placeholder={strings.newHabitPlaceholder}
              value={newName}
            />
          </div>
          <button
            className={BUTTON_PRIMARY}
            disabled={
              newName.trim() === "" || state.habits.length >= MAX_HABITS
            }
            type="submit"
          >
            {strings.addHabit}
          </button>
        </form>
        {state.habits.length >= MAX_HABITS ? (
          <p className={HINT}>{t(strings.limitReached, { max: MAX_HABITS })}</p>
        ) : null}

        {state.habits.length === 0 ? (
          <Notice>{strings.emptyState}</Notice>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="border-collapse text-sm">
              <thead>
                <tr>
                  <th
                    className="sticky left-0 z-10 min-w-48 border-border border-b bg-card px-3 py-2 text-left font-medium text-foreground text-xs uppercase tracking-wide"
                    scope="col"
                  >
                    {strings.habitColumn}
                  </th>
                  {Array.from({ length }, (_, index) => {
                    const dayOfMonth = index + 1;
                    const iso = isoOfMonthDay(
                      view.year,
                      view.month,
                      dayOfMonth
                    );
                    const isToday = iso !== null && iso === todayIso;
                    return (
                      <th
                        className={`border-border border-b px-0.5 py-1 text-center font-medium text-xs ${
                          isToday
                            ? "bg-primary/10 text-primary"
                            : "text-muted-foreground"
                        }`}
                        key={dayOfMonth}
                        scope="col"
                      >
                        {fmt(dayOfMonth)}
                      </th>
                    );
                  })}
                  <th className="border-border border-b px-1" scope="col" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => {
                  const name = habitName(rowIndex);
                  return (
                    <tr key={row.habit.id}>
                      <th
                        className="sticky left-0 z-10 border-border border-b bg-card px-2 py-1"
                        scope="row"
                      >
                        <input
                          aria-label={t(strings.habitNameLabel, {
                            number: rowIndex + 1,
                          })}
                          className={`${INPUT} min-w-44 py-1`}
                          maxLength={MAX_NAME_LENGTH}
                          onChange={(event) =>
                            renameHabit(row.habit.id, event.target.value)
                          }
                          value={row.habit.name}
                        />
                      </th>
                      {Array.from({ length }, (_, index) => {
                        const iso = isoOfMonthDay(
                          view.year,
                          view.month,
                          index + 1
                        );
                        const ticked =
                          iso !== null &&
                          (state.done[row.habit.id] ?? []).includes(iso);
                        const date = dayFormat.format(
                          new Date(Date.UTC(view.year, view.month, index + 1))
                        );
                        return (
                          <td
                            className="border-border border-b p-0.5 text-center"
                            key={iso ?? index}
                          >
                            <button
                              aria-label={t(strings.cellLabel, {
                                habit: name,
                                date,
                              })}
                              aria-pressed={ticked}
                              className={`h-7 w-7 rounded border text-xs transition-colors ${
                                ticked
                                  ? "border-primary bg-primary text-primary-foreground"
                                  : "border-border bg-background hover:bg-muted"
                              }`}
                              disabled={iso === null}
                              onClick={() => iso && toggle(row.habit.id, iso)}
                              type="button"
                            >
                              {ticked ? "✓" : ""}
                            </button>
                          </td>
                        );
                      })}
                      <td className="border-border border-b px-2">
                        <button
                          aria-label={t(
                            armed === row.habit.id
                              ? strings.confirmRemove
                              : strings.removeHabit,
                            { name }
                          )}
                          className={BUTTON_SMALL}
                          onClick={() => removeHabit(row.habit.id)}
                          type="button"
                        >
                          {armed === row.habit.id ? "?" : "×"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {rows.length > 0 ? (
          <div aria-live="polite" className="space-y-3">
            <h3 className="font-semibold text-foreground text-lg">
              {strings.statsHeading}
            </h3>
            <DataTable
              head={[
                strings.statsHabit,
                strings.statsMonth,
                strings.statsRate,
                strings.statsCurrent,
                strings.statsLongest,
              ]}
              rows={rows.map((row, index) => [
                habitName(index),
                t(strings.monthDone, {
                  done: fmt(row.summary.done),
                  elapsed: fmt(row.summary.elapsed),
                }),
                row.summary.percent === null
                  ? strings.notStarted
                  : t(strings.rateValue, { percent: fmt(row.summary.percent) }),
                tPlural(strings.dayCount, locale, row.current, {
                  count: fmt(row.current),
                }),
                tPlural(strings.dayCount, locale, row.longest, {
                  count: fmt(row.longest),
                }),
              ])}
            />
            <p className={HINT}>{strings.streakNote}</p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyText}
              text={summaryText}
            />
          </div>
        ) : null}
        <p className={HINT}>
          {canSave ? strings.storageNote : strings.storageUnavailable}
        </p>
      </section>

      <section className="space-y-5">
        <div className="space-y-5 print:hidden">
          <h2 className={WIDGET_H2}>{strings.printHeading}</h2>
          <p className={HINT}>{strings.sheetHint}</p>
          <div className={`${CARD} grid gap-4 sm:grid-cols-2 lg:grid-cols-3`}>
            <TextField
              id="ht-title"
              label={strings.sheetTitleLabel}
              onChange={setTitle}
              value={title}
            />
            <SelectField
              id="ht-blank"
              label={strings.blankRowsLabel}
              onChange={setBlankRows}
              options={BLANK_ROW_CHOICES.map((value) => ({
                value,
                label: value,
              }))}
              value={blankRows}
            />
            <PaperControls
              idPrefix="ht"
              onPaper={setPaper}
              paper={paper}
              strings={strings}
            />
          </div>
          <Checkbox
            checked={showTicks}
            label={strings.includeTicks}
            onChange={setShowTicks}
          />
          {layout.cramped ? <Notice>{strings.tooManyRows}</Notice> : null}
          <PrintBar
            fileName={`${strings.fileName}-${view.year}-${view.month + 1}`}
            pageTotal={1}
            paper={paper}
            strings={strings}
            svgRef={svgRef}
          />
        </div>
        <PrintablePages
          className="max-w-4xl"
          geometry={geometry}
          label={strings.previewLabel}
          pages={[
            <HabitSheetArt accent={ACCENT} key="sheet" layout={layout} />,
          ]}
          svgRef={svgRef}
        />
      </section>
    </div>
  );
}
